
import { of as observableOf, Subject } from 'rxjs'

import { mdiClose, mdiShuffleVariant } from '@mdi/js'
import Icon from '@mdi/react'
import { getQuotes } from 'admin/services/quotes/actions'
import CollapsibleTextInput from 'components/CollapsibleTextInput'
import { CuratedFolderListView } from 'components/CuratedFolderView'
import EmptyView from 'components/EmptyView'
import { useMountEffect } from 'hooks/useMountEffect'
import { StatusIdea } from 'interfaces'
import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { MIN_SEARCH_QUERY_LENGTH } from 'routes/find/routes/quotes/components/QuotesSearch'
import { SuggestedQuoteSearches } from 'routes/find/routes/quotes/components/SuggestedQuoteSearches'
import { initialState, QuotesContext, quotesReducer, saveSearches, updateSearch } from 'routes/find/routes/quotes/QuotesContext'
import { QUOTES } from 'routes/find/routes/statuses/statuses.config'
import { catchError, filter, mergeMap, switchMap, tap } from 'rxjs/operators'
import { getRandomQuotes, saveQuoteSearch } from 'services/content/quotes/actions'
import { StoreThunkDispatch } from 'store/state'
import styles from './QuotesPanel.pcss'
interface QuotesPanelProps {
  className?: string
}

export function QuotesPanel(props: QuotesPanelProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const save$ = useRef<Subject<string>>(undefined)
  const [{ search, savedSearches }, quotesDispatch] = useReducer(quotesReducer, initialState)
  const [items, setItems] = useState<StatusIdea[]>([])
  const [listKey, setListKey] = useState(0)
  const lastSearchRef = useRef('')
  const inputRef = useRef<HTMLInputElement>(null)
  const fetch$ = useRef<Subject<{ query: string, page: number, tag?: string }>>(undefined)

  useMountEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(tap(({ query }) => {
        lastSearchRef.current = query
        quotesDispatch(updateSearch({ active: true, loading: true }))
      })).pipe(
        switchMap(({ query, page, tag }) => {
          const request = query || tag ? dispatch(getQuotes(page, query, tag)) : dispatch(getRandomQuotes())
          return request
            .pipe(catchError(() => {
              return observableOf([])
            }))
        }))
      .subscribe((data: StatusIdea[]) => {
        setItems(current => ([...current, ...data]))
        quotesDispatch(updateSearch({ hasNextPage: data.length > 0, loading: false }))
      })

    return () => {
      fetch$.current?.unsubscribe()
    }
  })

  useMountEffect(() => {
    save$.current = new Subject()
    save$.current.pipe(
      filter(q => q.length >= MIN_SEARCH_QUERY_LENGTH),
      mergeMap(q => {
        quotesDispatch(saveSearches([q]))
        return dispatch(saveQuoteSearch(q))
      }),
      catchError(() => observableOf({}))
    )
      .subscribe()

    return () => {
      save$.current?.unsubscribe()
    }
  })

  useEffect(() => {
    fetch$.current?.next({ query: search.query, page: search.page, tag: search.tag })
  }, [search.query, search.page, search.tag])

  const refreshResults = useCallback(() => {
    fetch$.current?.next({ query: search.query, page: 0, tag: search.tag })
  }, [search.query, search.tag])

  const loadNextPage = useCallback(() => {
    if (!search.loading && search.hasNextPage && !search.tag) { // tag search has no paging
      quotesDispatch(updateSearch({ page: search.page + 1 }))
    }
  }, [search.loading, search.hasNextPage, search.tag, search.page])

  const onQueryChange = useCallback((value: string, isTag?: boolean, forceReset?: boolean, clearTag?: boolean) => {
    const valueChanged = forceReset || value !== lastSearchRef.current || Boolean(isTag) !== Boolean(search.tag)
    const shouldClearTag = clearTag || (isTag && value === search.tag)
    if (valueChanged) {
      setItems([])
      setListKey(k => k + 1)
    }
    quotesDispatch(updateSearch({
      query: isTag ? search.query : value,
      tag: shouldClearTag ? '' : isTag ? value : search.tag,
      ...(valueChanged && { page: 0 }),
      ...(valueChanged && { hasNextPage: true })
    }))
    if (!valueChanged && forceReset) {
      refreshResults()
    }
  }, [search, refreshResults])

  const shuffle = () => {
    if (search.query) {
      onQueryChange('')
    } else if (search.page === 0) {
      setItems([])
      setListKey(k => k + 1)
      fetch$.current?.next({ query: '', page: 0 })
    } else {
      setItems([])
      setListKey(k => k + 1)
      quotesDispatch(updateSearch({ page: 0 }))
    }
  }

  const onSearch = (value: string) => {
    const q = value.trim()
    if (q.length > 0) {
      save$.current?.next(value)
    }
    onQueryChange(value)
  }

  const onTagClick = (q: string, isTag: boolean) => {
    onQueryChange(q, isTag, false, !isTag)
    if (!isTag && inputRef.current) {
      inputRef.current.value = q
    }
  }

  const onQueryClick = (q: string) => {
    const next = search.query === q ? '' : q
    onQueryChange(next, false, true)
    if (inputRef.current) {
      inputRef.current.value = next
    }
  }

  const clearSearch = () => {
    onSearch('')
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const contextValue = useMemo(
    () => ({ search, savedSearches, dispatch: quotesDispatch }),
    [search, savedSearches]
  )

  return (
    <QuotesContext.Provider value={contextValue}>
      <section className={`${styles.section} ${props.className || ''}`} data-testid="quotes-panel">
        <header className={styles['quotes-header']}>
          <h2 className={`${styles.title} text-ellipsis`}>
            <FormattedMessage id="find.titles.quotes" />
          </h2>
          <span className={styles['text-btn']} data-testid="btn-random" onClick={shuffle}>
            <Icon size="20px" path={mdiShuffleVariant} />
            <FormattedMessage id="label.generic.random" />
          </span>
        </header>
        <SuggestedQuoteSearches
          activeQuery={search.query}
          tag={search.tag}
          searchElement={(
            <CollapsibleTextInput
              changeOnEnter
              initialActive
              className={styles.searchbox}
              ref={inputRef}
              id="quotes-panel-search"
              activeClassName={styles['search-active']}
              actionButton={(
                <div className={`${styles['btn-clear']} ${search.query ? '' : styles.hidden}`} onClick={clearSearch}>
                  <Icon path={mdiClose} color="rgba(0, 0, 0, 0.54)" size="20px" />
                </div>
              )}
              placeholder={intl.formatMessage({ id: 'find.quotes.search-hint' })}
              onValueChanged={onSearch}
            />
          )}
          onQueryClick={onQueryClick}
          onSuggestedSearchClick={onTagClick}
        />
        <div className={styles.content}>
          <NavLink to={`/content/quotes${search.query ? '?q=' + search.query : ''}`} className={styles.link}>
            {search.query && <FormattedMessage id="general.carousel.nav.see-all-results" values={{ label: search.query }} />}
            {!search.query && (
              <Fragment>
                <FormattedMessage id="general.carousel.nav.see-all" />
                <span className={styles['query-text']}>
                  {search.query || <FormattedMessage id="find.titles.quotes" />}
                </span>
              </Fragment>
            )}
          </NavLink>
          <CuratedFolderListView
            stream={QUOTES as any}
            items={items}
            key={listKey}
            loading={search.loading}
            onScrollLimit={loadNextPage}
          />
          {!search.loading && search.query && items.length === 0 && (
            <EmptyView
              carousel
              className={styles['error-overlay']}
              title={<FormattedMessage id="content.msg.quotes-search-empty" />}
              subtitle={<FormattedMessage id="content.msg.quotes-search-empty-subtitle" />}
            />
          )}
        </div>
      </section>
    </QuotesContext.Provider>
  )
}

export default QuotesPanel
